// Manager chrome — ONE route tree (`/manager/*`), two layouts chosen by
// VIEWPORT, not User-Agent (PO direction, 2026-07-31).
//
// Below `md` the manager gets the bottom-nav app shell ("minimum chrome, a
// real conversational interface, navigation at the bottom", PO 2026-07-30);
// at `md` and above, the existing desktop `Shell` (sidebar at `lg`, pill top
// nav between). Both render the same `<Outlet />` — every `/manager/*` page
// is reachable at every width.
//
// WHY THIS REPLACED THE UA GATE: the bottom-nav shell used to live on its own
// `/manager/mobile/*` route tree entered only by a User-Agent match (at the
// sign-in landing redirect plus a handful of `deviceGuardLoader`s). That made
// it unreachable in practice — a reload, a bookmark, an email deep-link, or
// the client-side `landingPathFor` all landed on `/manager` and rendered
// desktop chrome on a phone, and a narrow desktop window could never see the
// app shell at all. Meanwhile `ManagerDashboard` had grown a SECOND,
// viewport-gated (`md:hidden`) mobile branch, so there were two mobile
// Manager UIs and the wrong one was winning. Nothing security-relevant ever
// depended on the UA check — it only picked a layout — so the layout decision
// now lives where layout decisions belong, in a media query. The old
// `/manager/mobile/*` paths survive as redirects (see `router.tsx`) so live
// email deep-links and bookmarks keep working.
//
// There is NO JS branch here — `Shell` renders one element tree and toggles
// chrome with breakpoint classes. See its header for why that is a
// correctness requirement: a branch returning two different component types
// unmounts the whole page on rotation, taking every unsaved form field with
// it. The only thing that varies per route below is a className.

import { useLocation } from 'react-router';
import { Shell, FULL_BLEED_CONTENT_CLASSNAME } from '@/components/Shell.tsx';
import type { NavItem } from '@/components/AppNav.tsx';
import { MaeveAvatar } from '@/components/brand/MaeveAvatar.tsx';
import type { MobileTab } from '@/components/MobileTabBar.tsx';
import {
  UsersIcon,
  PlusIcon,
  SlidersIcon,
  TrainingIcon,
  ChatIcon,
  HomeIcon,
  DocIcon,
} from '@/components/ui/icons.tsx';

const MANAGER_NAV: NavItem[] = [
  // "Home", not "My team", since PO 2026-08-06 — `/manager` is the four
  // action cards at every width now, and the pathway list it used to be
  // moved to `/manager/team` below.
  { to: '/manager', label: 'Home', end: true, icon: <HomeIcon /> },
  { to: '/manager/team', label: 'My team', icon: <UsersIcon /> },
  // Frontdoor into Manager Sub-mode B guidance — a support-pathway picker in
  // front of the existing pathway-scoped `ManagerMaeveGuidance`, not a new
  // pathway-less Maeve mode (see ManagerAskMaeve.tsx for why). NB the
  // case-less DRAFTER (`/manager/draft`, PO 2026-08-06) is a different thing
  // and not a counter-example: it is a one-shot form against a closed-enum
  // endpoint, not a conversation, and it carries no identity.
  { to: '/manager/maeve', label: 'Ask Maeve', icon: <ChatIcon /> },
  { to: '/manager/cases/new', label: 'New support pathway', icon: <PlusIcon /> },
  // TrainingIcon, not LifebuoyIcon — the latter is CATEGORY_ICON's "crisis"
  // glyph on this exact page (ResourceCatalogGrid.tsx); reusing it for the
  // nav entry itself would mean the same icon meaning two different things
  // on one screen.
  { to: '/manager/resources', label: 'Resources', icon: <TrainingIcon /> },
  // Read-only mirror of HR's Policies tab (PO 2026-08-06) — same DocIcon as
  // `/hr/policies`. Approved-only; upload/approve/delete stay HR_Admin-only.
  { to: '/manager/policies', label: 'Policies', icon: <DocIcon /> },
  { to: '/manager/settings', label: 'Settings', icon: <SlidersIcon /> },
];

// Four tabs, each a real `/manager/*` path — the PO's mocked-up set, and
// deliberately a SUBSET of `MANAGER_NAV`. There is no "More" slot, so the
// `MANAGER_NAV` entries that don't get a tab — **New support pathway, Policies,
// Settings, and Sign out** — are surfaced on the Team tab instead
// (`ManagerTeam.tsx`: a header action plus an account block mirroring the
// desktop sidebar's footer). Below `md` the desktop `Shell` isn't rendered at
// all, so without that they would be unreachable on a phone entirely.
//
// Four is a DESIGN constraint (it's what the PO mocked up), not a layout one:
// the Employee portal runs five tabs — Home/Maeve/Check-ins/Resources/More —
// at 375px with no truncation. So if a "More" tab is ever approved here,
// there's room for it, and these four belong on it.
const MANAGER_TABS: MobileTab[] = [
  { to: '/manager', label: 'Home', icon: <HomeIcon size={22} />, end: true },
  // Maeve's face stands in for a tab icon here rather than ChatIcon — the
  // face is the brand mark (Logo.tsx uses the same component), so the tab
  // reads as "go to Maeve" rather than a generic chat glyph.
  { to: '/manager/maeve', label: 'Maeve', icon: <MaeveAvatar size={22} rings={false} /> },
  { to: '/manager/team', label: 'Team', icon: <UsersIcon size={22} /> },
  { to: '/manager/resources', label: 'Resources', icon: <TrainingIcon size={22} /> },
];

// The one page that paints edge-to-edge inside the app shell: the Maeve chat
// owns the full viewport between header and tab bar and scrolls internally,
// so the shell must not add page padding or a second scroll container around
// it. Every other `/manager/*` page is a normal padded document.
const FULL_BLEED_PATHS = new Set(['/manager/maeve']);

export function ManagerShell() {
  const { pathname } = useLocation();
  // Full-bleed BELOW `md` only — at `md`+ the chat is a bounded column inside
  // the normal padded page (see `ManagerAskMaeve`). Expressed as classNames so
  // the tree stays constant.
  const fullBleed = FULL_BLEED_PATHS.has(pathname.replace(/\/$/, ''));

  return (
    <Shell
      chipLabel="MANAGER"
      nav={MANAGER_NAV}
      routeId="manager-shell"
      tabs={MANAGER_TABS}
      tabsLabel="Manager"
      breakpoint="md"
      contentClassName={
        fullBleed
          ? FULL_BLEED_CONTENT_CLASSNAME.md
          : 'mx-auto w-full max-w-6xl px-4 py-5 md:py-6 lg:px-8 lg:py-10'
      }
    />
  );
}
